import { CalculationMethod, Coordinates, HighLatitudeRule, Madhab, PrayerTimes } from 'adhan';
import type { EidSalah, JamaahRule, JumuahSlot, Masjid, Prayer, Salah, TimetableDay } from './types';
import { PRAYERS, SALAH } from './types';
import { addDays, fromMinutes, hhmm, minutesOf, normaliseTime, parseYmd, weekdayOf, ymd, zonedToInstant } from './time';

export const PRAYER_LABEL: Record<Prayer, string> = {
  fajr: 'Fajr', sunrise: 'Sunrise', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha',
};

export interface DayTimes {
  date: string;
  /** Start times, HH:MM in the masjid's zone, from the uploaded timetable. Empty when the timetable has no time. */
  start: Record<Prayer, string>;
  /** Jama'ah times, HH:MM. Missing when the timetable gives none for that salah. */
  jamaah: Partial<Record<Salah, string>>;
  /** Whether the timetable has this day at all. */
  listed: boolean;
  isFriday: boolean;
  /** On Fridays, the Jumu'ah jama'ahs (first and second), from the timetable or the Jumu'ah settings. */
  jumuah: JumuahSlot[];
  eid: EidSalah | null;
  /** Ramadan times, when the timetable has them. Iftar is Maghrib's start. */
  sehri?: string;
  iftar?: string;
  tarawih?: string;
}

export type TimetableIndex = ReadonlyMap<string, TimetableDay>;
export const indexTimetable = (days: readonly TimetableDay[]): TimetableIndex => new Map(days.map((d) => [d.date, d]));

const HIGH_LAT = { MiddleOfTheNight: HighLatitudeRule.MiddleOfTheNight, SeventhOfTheNight: HighLatitudeRule.SeventhOfTheNight, TwilightAngle: HighLatitudeRule.TwilightAngle } as const;

/** Start times from astronomical calculation for the masjid's location and settings. */
export function calculateStarts(masjid: Masjid, day: string): Record<Prayer, string> {
  const factory = CalculationMethod[masjid.prayer.method] ?? CalculationMethod.MoonsightingCommittee;
  const params = factory();
  params.madhab = masjid.prayer.madhab === 'Hanafi' ? Madhab.Hanafi : Madhab.Shafi;
  params.highLatitudeRule = HIGH_LAT[masjid.prayer.highLatitudeRule] ?? HighLatitudeRule.recommended(new Coordinates(masjid.latitude, masjid.longitude));
  for (const p of PRAYERS) params.adjustments[p] = Math.round(masjid.prayer.adjustments[p] ?? 0);
  const { year, month, day: d } = parseYmd(day);
  // adhan reads the calendar date from local date parts, so build the date locally (time zone independent).
  const times = new PrayerTimes(new Coordinates(masjid.latitude, masjid.longitude), new Date(year, month - 1, d, 12), params);
  const tz = masjid.timezone;
  return { fajr: hhmm(times.fajr, tz), sunrise: hhmm(times.sunrise, tz), dhuhr: hhmm(times.dhuhr, tz), asr: hhmm(times.asr, tz), maghrib: hhmm(times.maghrib, tz), isha: hhmm(times.isha, tz) };
}

/** Applies a jama'ah rule to a start time. Never returns a time before the start. */
export function applyJamaahRule(start: string, rule: JamaahRule | undefined): string | undefined {
  if (!rule || rule.mode === 'none') return undefined;
  const s = minutesOf(start);
  let j = s;
  if (rule.mode === 'offset') j = s + Math.max(0, rule.minutes ?? 0);
  else if (rule.mode === 'fixed') {
    const f = normaliseTime(rule.time);
    j = f ? minutesOf(f) : s;
    if (j < s) j = s + Math.max(0, rule.minutes ?? 0); // fixed time already passed the start (e.g. summer Isha)
  }
  const r = Math.max(0, Math.round(rule.roundTo ?? 0));
  if (r > 1) j = Math.ceil(j / r) * r;
  return fromMinutes(Math.min(j, 23 * 60 + 59));
}

/**
 * One day's times. Everything comes from the masjid's uploaded timetable: nothing is calculated, so a screen never
 * shows a time the masjid didn't set. Days missing from the timetable show no times.
 */
export function dayTimes(masjid: Masjid, timetable: TimetableIndex, day: string): DayTimes {
  const row = timetable.get(day);
  const start = {} as Record<Prayer, string>;
  for (const p of PRAYERS) start[p] = normaliseTime(row?.[p]) ?? '';
  const jamaah: Partial<Record<Salah, string>> = {};
  for (const s of SALAH) {
    const j = normaliseTime(row?.[`${s}Jamaah` as const]);
    if (j) jamaah[s] = j;
  }
  const isFriday = weekdayOf(day) === 5;
  return {
    date: day, start, jamaah, listed: !!row, isFriday,
    jumuah: isFriday ? jumuahFor(masjid, row) : [],
    eid: masjid.prayer.eid.find((e) => e.date === day) ?? null,
    sehri: normaliseTime(row?.sehri) ?? undefined,
    iftar: start.maghrib || undefined,
    tarawih: normaliseTime(row?.tarawih) ?? undefined,
  };
}

/** A Friday's Jumu'ah jama'ahs: the timetable's Jumu'ah 1 and 2 when it has them, otherwise the Jumu'ah settings. */
export function jumuahFor(masjid: Masjid, row: TimetableDay | undefined): JumuahSlot[] {
  const set = masjid.prayer.jumuah;
  const csv = [normaliseTime(row?.jumuah1), normaliseTime(row?.jumuah2)];
  if (!csv[0] && !csv[1]) return set.filter((s) => normaliseTime(s.jamaah));
  // The khutbah time from settings is kept only when it comes before that jama'ah.
  return csv.flatMap((j, i) => {
    if (!j) return [];
    const k = normaliseTime(set[i]?.khutbah);
    return [{ label: i === 0 ? '1st Jumu‘ah' : '2nd Jumu‘ah', khutbah: k && k < j ? k : '', jamaah: j }];
  });
}

const toMin = (t: string) => +t.slice(0, 2) * 60 + +t.slice(3, 5);
const fromMin = (m: number) => { const x = ((m % 1440) + 1440) % 1440; return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`; };

/**
 * What the Jumu'ah card shows. When the uploaded timetable has Jumu'ah times for the coming Friday, those (with the
 * khutbah times from settings, or the usual gap before the jama'ah); otherwise the settings. `friday` names the
 * timetable's Friday the times came from, or is null.
 */
export function jumuahForEditing(masjid: Masjid, timetable: readonly TimetableDay[], today: string): { slots: JumuahSlot[]; friday: string | null } {
  const set = masjid.prayer.jumuah;
  const row = timetable.find((d) => d.date >= today && weekdayOf(d.date) === 5 && (normaliseTime(d.jumuah1) || normaliseTime(d.jumuah2)));
  if (!row || row.date > addDays(today, 6)) return { slots: structuredClone(set), friday: null };
  const csv = [normaliseTime(row.jumuah1), normaliseTime(row.jumuah2)];
  const slots = csv.flatMap((j, i) => {
    if (!j) return [];
    const own = set[i];
    const gap = own && normaliseTime(own.khutbah) && normaliseTime(own.jamaah) ? toMin(normaliseTime(own.jamaah)!) - toMin(normaliseTime(own.khutbah)!) : 15;
    const k = normaliseTime(own?.khutbah);
    return [{ label: own?.label || (i === 0 ? 'First Jumu‘ah' : 'Second Jumu‘ah'), khutbah: k && k < j ? k : fromMin(toMin(j) - (gap > 0 && gap <= 90 ? gap : 15)), jamaah: j }];
  });
  return { slots, friday: row.date };
}

/**
 * Edits made in the Jumu'ah card, carried into the timetable: every Friday from `today` whose Jumu'ah times were
 * `before` (the times the card showed) gets the new ones. Fridays with other times (a summer timetable, say) are left
 * as they are. Returns only the days that changed.
 */
export function applyJumuahEdit(timetable: readonly TimetableDay[], today: string, before: readonly string[], after: readonly string[]): TimetableDay[] {
  const same = (d: TimetableDay) => (normaliseTime(d.jumuah1) ?? '') === (before[0] ?? '') && (normaliseTime(d.jumuah2) ?? '') === (before[1] ?? '');
  const out: TimetableDay[] = [];
  for (const d of timetable) {
    if (d.date < today || weekdayOf(d.date) !== 5 || !same(d)) continue;
    const next: TimetableDay = { ...d };
    delete next.jumuah1; delete next.jumuah2;
    if (after[0]) next.jumuah1 = after[0];
    if (after[1]) next.jumuah2 = after[1];
    if (next.jumuah1 !== d.jumuah1 || next.jumuah2 !== d.jumuah2) out.push(next);
  }
  return out;
}

/** The next Friday's Jumu'ah (today if it's Friday and the last jama'ah hasn't passed), from the timetable. */
export function nextJumuah(masjid: Masjid, timetable: TimetableIndex, now: Date): { day: string; slots: JumuahSlot[] } | null {
  const today = ymd(now, masjid.timezone);
  for (let i = 0; i < 8; i++) {
    const day = addDays(today, i);
    if (weekdayOf(day) !== 5) continue;
    const slots = jumuahFor(masjid, timetable.get(day));
    const last = slots.at(-1);
    if (i === 0 && last && zonedToInstant(day, last.jamaah, masjid.timezone).getTime() < now.getTime() - 20 * 60_000) continue;
    return slots.length ? { day, slots } : null;
  }
  return null;
}

export interface PrayerMoment { prayer: Prayer; kind: 'start' | 'jamaah'; at: Date; day: string }

export interface PrayerState {
  today: DayTimes;
  /** The salah whose time we are in (null between sunrise and Dhuhr). */
  current: Salah | null;
  /** Next start time (Sunrise counts, so Fajr's "ends at" is visible). */
  nextStart: PrayerMoment;
  /** Next jama'ah, if the masjid shows jama'ah times. */
  nextJamaah: PrayerMoment | null;
  /** Prayer to highlight on screen: the next salah to be prayed in congregation, else the next start. */
  highlight: Prayer;
  /** True when the timetable has no times coming up (nothing uploaded yet): countdowns show a dash. */
  empty: boolean;
}

function moments(masjid: Masjid, t: DayTimes): PrayerMoment[] {
  const out: PrayerMoment[] = [];
  for (const p of PRAYERS) if (t.start[p]) out.push({ prayer: p, kind: 'start', at: zonedToInstant(t.date, t.start[p], masjid.timezone), day: t.date });
  for (const s of SALAH) {
    // On Fridays, Dhuhr's jama'ahs are the Jumu'ahs (both, when there are two).
    const js = s === 'dhuhr' && t.isFriday && t.jumuah.length ? t.jumuah.map((x) => normaliseTime(x.jamaah)).filter((x): x is string => !!x) : [t.jamaah[s]].filter((x): x is string => !!x);
    for (const j of js) out.push({ prayer: s, kind: 'jamaah', at: zonedToInstant(t.date, j, masjid.timezone), day: t.date });
  }
  return out.sort((a, b) => a.at.getTime() - b.at.getTime());
}

/** Where we are in the prayer day at `now`. Pure: same inputs always give the same answer. */
export function prayerState(masjid: Masjid, timetable: TimetableIndex, now: Date, getDay: (day: string) => DayTimes = (d) => dayTimes(masjid, timetable, d)): PrayerState {
  const day = ymd(now, masjid.timezone);
  const today = getDay(day);
  const t = now.getTime();
  // Look ahead a few days, so a gap in the timetable doesn't stop the countdown.
  let all = [...moments(masjid, today), ...moments(masjid, getDay(addDays(day, 1)))];
  for (let i = 2; i < 8 && !all.some((m) => m.kind === 'start' && m.at.getTime() > t); i++) all = [...all, ...moments(masjid, getDay(addDays(day, i)))];
  const found = all.find((m) => m.kind === 'start' && m.at.getTime() > t);
  const empty = !found;
  const nextStart = found ?? { prayer: 'fajr' as Prayer, kind: 'start' as const, at: new Date(t + 86_400_000), day: addDays(day, 1) };
  const nextJamaah = all.find((m) => m.kind === 'jamaah' && m.at.getTime() > t) ?? null;

  let current: Salah | null = null;
  const nowMin = minutesOf(hhmm(now, masjid.timezone));
  const startMin = (p: Prayer) => (today.start[p] ? minutesOf(today.start[p]) : NaN);
  if (!today.start.fajr || !today.start.isha) current = null;
  else if (nowMin < startMin('fajr')) current = 'isha';
  else if (nowMin < startMin('sunrise')) current = 'fajr';
  else if (nowMin < startMin('dhuhr')) current = null;
  else if (nowMin < startMin('asr')) current = 'dhuhr';
  else if (nowMin < startMin('maghrib')) current = 'asr';
  else if (nowMin < startMin('isha')) current = 'maghrib';
  else current = 'isha';

  // Highlight the salah still to be prayed in jama'ah if its jama'ah hasn't happened yet; otherwise the next one.
  let highlight: Prayer = nextStart.prayer;
  if (current && nextJamaah && nextJamaah.prayer === current && nextJamaah.at.getTime() < nextStart.at.getTime()) highlight = current;
  return { today, current, nextStart, nextJamaah, highlight, empty };
}
